// Portal Invite Service - Local implementation like internal users
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import crypto from 'crypto-js';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const toDateOrNull = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Public signup URL for an invite token. Single source of truth for link shape. */
export const buildInviteSignupUrl = (token, email) =>
  `${window.location.origin}/signup?token=${token}&email=${encodeURIComponent(email || '')}`;

/**
 * Existing pending, unexpired invite for this account + email, if any.
 * Used to keep repeat "Add Team Member" clicks from stacking up duplicate invites.
 */
export const findPendingPortalInvite = async (leadId, email) => {
  const targetEmail = normalizeEmail(email);
  if (!leadId || !targetEmail) return null;

  const snapshot = await getDocs(
    query(collection(db, 'portalInvites'), where('leadId', '==', leadId))
  );

  const now = new Date();
  const matches = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((invite) => normalizeEmail(invite.email) === targetEmail)
    .filter((invite) => (invite.status || 'pending') === 'pending')
    .filter((invite) => {
      const expiresAt = toDateOrNull(invite.expiresAt);
      return !expiresAt || expiresAt > now;
    })
    .sort((a, b) => {
      const aDate = toDateOrNull(a.createdAt)?.getTime() || 0;
      const bDate = toDateOrNull(b.createdAt)?.getTime() || 0;
      return bDate - aDate;
    });

  const existing = matches[0];
  if (!existing) return null;

  return {
    inviteId: existing.id,
    token: existing.token,
    signupUrl: buildInviteSignupUrl(existing.token, existing.email),
    expiresAt: toDateOrNull(existing.expiresAt)?.toISOString() || null,
    reused: true
  };
};

export const createPortalInvite = async ({
  leadId,
  email,
  firstName,
  lastName,
  company
}) => {
  try {
    // Reuse a live invite for the same person instead of creating a duplicate
    const existing = await findPendingPortalInvite(leadId, email);
    if (existing) return existing;

    // Generate a secure random token
    const token = crypto.lib.WordArray.random(32).toString();

    // Set expiration date (10 days from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 10);

    // Create invite document
    const inviteData = {
      token,
      leadId,
      email,
      firstName,
      lastName,
      company: company || 'Unknown Company',
      type: 'portal',
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: expiresAt,
      usedAt: null,
      usedBy: null
    };

    // Save invite to Firestore
    const inviteRef = await addDoc(collection(db, 'portalInvites'), inviteData);

    return {
      inviteId: inviteRef.id,
      token,
      signupUrl: buildInviteSignupUrl(token, email),
      expiresAt: expiresAt.toISOString(),
      reused: false
    };
  } catch (error) {
    console.error('Error creating portal invite:', error);
    throw error;
  }
};
